import React, { useEffect, useMemo, useRef } from "react"
import { Alert, CircularProgress, Container, Stack, Typography } from "@mui/material"
import { usePromptCatalog } from "@/shared/hooks/usePromptCatalog"
import { useAuth } from "@/features/auth/context/AuthContext"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import GenerateWithAIFab from "@/shared/components/generate-with-ai/GenerateWithAIFab"
import PromptCardsGrid from "@/features/bulkedit/components/PromptCardsGrid"
import SelectedImagesGrid from "@/features/bulkedit/components/SelectedImagesGrid"
import DeleteImageDialog from "@/features/bulkedit/components/DeleteImageDialog"
import { useBulkEdit } from "@/features/bulkedit/hooks/useBulkEdit"
import { MAX_BULK_IMAGES } from "@/features/bulkedit/types"

const BulkEditPage = () => {
  const { prompts, loading, error } = usePromptCatalog()
  const { user } = useAuth()
  const { showSnackbar } = useSnackbar()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const {
    selectedPromptId,
    selectedImages,
    imageToDeleteId,
    generating,
    setSelectedPromptId,
    setImageToDeleteId,
    handleFileChange,
    handleConfirmDeleteImage,
    handleGenerateWithAI
  } = useBulkEdit({ userId: user?.uid ?? null, maxImages: MAX_BULK_IMAGES, notify: showSnackbar })

  useEffect(() => {
    document.title = "Bulk Edit | ListYourProduct"
  }, [])

  const selectedPrompt = useMemo(
    () => prompts.find(prompt => prompt.id === selectedPromptId) ?? null,
    [prompts, selectedPromptId]
  )

  const handleUsePrompt = (promptId: string) => {
    setSelectedPromptId(promptId)
    fileInputRef.current?.click()
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleFileChange} />

      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight={700}>
            Bulk Edit Prompts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a prompt from the list below, then choose one or more images to apply it to in bulk.{" "}
          </Typography>
        </Stack>
        {selectedPrompt && <Alert severity="info">Selected prompt: {selectedPrompt.name}</Alert>}

        <SelectedImagesGrid images={selectedImages} maxImages={MAX_BULK_IMAGES} onRequestDelete={setImageToDeleteId} />

        {loading && <CircularProgress />}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}

        {!loading && !error && prompts.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No prompts found.
          </Typography>
        )}

        {!loading && !error && prompts.length > 0 && (
          <PromptCardsGrid prompts={prompts} onUsePrompt={handleUsePrompt} />
        )}
      </Stack>

      <GenerateWithAIFab
        onClick={handleGenerateWithAI}
        disabled={!selectedPromptId || selectedImages.length === 0 || generating}
        loading={generating}
        tooltip={!selectedPromptId || selectedImages.length === 0 ? "Select a prompt and images" : "Generate with AI"}
        label="Generate with AI"
      />

      <DeleteImageDialog
        open={Boolean(imageToDeleteId)}
        onClose={() => setImageToDeleteId(null)}
        onConfirm={handleConfirmDeleteImage}
      />
    </Container>
  )
}

export default BulkEditPage
